import {
  PlusCircle,
  UserCheck,
  RefreshCw,
  MessageSquare,
  Clock3,
  Paperclip,
  type LucideIcon,
} from "lucide-react";
import { format } from "date-fns";
import { HistoryEvent } from "@/api/complaint.api";

const EVENT_ICON: Record<string, LucideIcon> = {
  CREATED: PlusCircle,
  ASSIGNED: UserCheck,
  REASSIGNED: UserCheck,
  STATUS_CHANGE: RefreshCw,
  NOTE_ADDED: MessageSquare,
  HOURS_LOGGED: Clock3,
};

function describeEvent(event: HistoryEvent): string {
  switch (event.eventType) {
    case "CREATED":
      return "Complaint created";
    case "ASSIGNED":
      return "Assigned to technician";
    case "REASSIGNED":
      return "Reassigned to a different technician";
    case "STATUS_CHANGE":
      return `Status changed${event.previousStatus ? ` from ${event.previousStatus}` : ""} to ${event.newStatus}`;
    case "NOTE_ADDED":
      return event.remarks?.toLowerCase().startsWith("uploaded") ? "Attachment uploaded" : "Note added";
    case "HOURS_LOGGED":
      return `Logged ${event.workingHours} hour${event.workingHours === 1 ? "" : "s"} of work`;
    default:
      return event.eventType;
  }
}

export function ComplaintTimeline({ events }: { events: HistoryEvent[] }) {
  if (events.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
        No activity recorded yet.
      </p>
    );
  }

  return (
    <ol className="relative space-y-5 border-l border-border pl-5">
      {events.map((event) => {
        const Icon =
          EVENT_ICON[event.eventType] ??
          (event.eventType === "NOTE_ADDED" && event.remarks?.toLowerCase().startsWith("uploaded")
            ? Paperclip
            : MessageSquare);
        return (
          <li key={event.id} className="relative">
            <span className="absolute -left-[1.65rem] flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Icon className="h-3.5 w-3.5" />
            </span>
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-medium">{describeEvent(event)}</p>
              <time className="shrink-0 text-xs text-muted-foreground">
                {format(new Date(event.createdAt), "dd MMM yyyy, HH:mm")}
              </time>
            </div>
            {event.remarks && !event.remarks.toLowerCase().startsWith("uploaded") && (
              <p className="mt-0.5 text-sm text-muted-foreground">{event.remarks}</p>
            )}
            {event.employee && (
              <p className="mt-0.5 text-xs text-muted-foreground">by {event.employee.fullName}</p>
            )}
          </li>
        );
      })}
    </ol>
  );
}
